import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./TrovePaywall.module.scss"

/**
 * Trove — an ONBOARDING paywall whose entire job is the one thing no other
 * paywall in the set does: sell the *Ultimate* tier over Premium. The active
 * Slack directive ("sell the ultimate subscription plan, default to ultimate
 * but allow buying premium as well") is the spine of the concept, not a coat of
 * paint on it.
 *
 * The mechanic is a **tier lever**. Your new vault sits in the middle; a tactile
 * slider on the right runs from PREMIUM (bottom) up to ULTIMATE (top), and it
 * starts at the top. Push it and the vault *levels up* in front of you:
 *   • Room   — the capacity readout counts 50 GB → 1,000 GB
 *   • Order  — the loose memory grid gains subalbum labels + a search bar
 *              (Organization+: subalbums, search, duplicate cleanup)
 *   • Seal   — a second lock clasp snaps shut and the glow warms from
 *              premium-gold to the app's own Ultimate violet (#892bf5)
 * Slide back down to Premium and each of those honestly recedes. Nothing is
 * ever deleted or lost — the lever only chooses *how much vault you set up*.
 *
 * Divergence (vs. the ledger): the value frame is a **tier upgrade
 * (Premium → Ultimate: room + organization + security as one leap)** — untouched
 * by all prior concepts, none of which sell Ultimate. It deliberately avoids
 * Aurora's orbiting-core onboarding layout, and uses the Ultimate violet as the
 * hero colour (a palette no prior paywall owns). Register: confident abundance,
 * "set up your vault — keep the essentials, or keep all of it."
 *
 * Self-contained: renders on the built-in debug product data (Ultimate yearly
 * $119.99 / Premium yearly $35.99, both 7-day trial), reuses the bundled
 * `nimbus/` memory photos, no native injection.
 */

type Tier = "ultimate" | "premium"

// Memory tiles, reused from the bundled set so the screen is fully
// self-contained (no runtime fetches). Grouped into three illustrative
// subalbums that light up when Organization+ (Ultimate) is on.
const ALBUMS: { label: string; files: string[] }[] = [
  { label: "Family", files: ["mem1.jpg", "mem6.jpg", "mem11.jpg", "mem3.jpg"] },
  { label: "Travel", files: ["mem7.jpg", "mem2.jpg", "mem9.jpg", "mem14.jpg"] },
  {
    label: "Recent",
    files: ["mem5.jpg", "mem12.jpg", "mem4.jpg", "mem16.jpg"],
  },
]

const GB_PREMIUM = 50
const GB_ULTIMATE = 1000

const Glyph = ({
  name,
}: {
  name: "lock" | "search" | "arrow" | "close" | "check" | "spark" | "layers"
}) => {
  switch (name) {
    case "lock":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="4.5"
            y="10.4"
            width="15"
            height="10.2"
            rx="3"
            fill="currentColor"
          />
          <path
            d="M8 10.4V7.6a4 4 0 0 1 8 0v2.8"
            stroke="currentColor"
            strokeWidth="2.1"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      )
    case "search":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle
            cx="11"
            cy="11"
            r="6.4"
            stroke="currentColor"
            strokeWidth="2.1"
          />
          <path
            d="m16 16 4.4 4.4"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
          />
        </svg>
      )
    case "arrow":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 12h13m0 0-5-5m5 5-5 5"
            stroke="currentColor"
            strokeWidth="2.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "close":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      )
    case "check":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m5 12.5 4.5 4.5L19 7"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "spark":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2.5c.6 4.7 2.3 6.4 7 7-4.7.6-6.4 2.3-7 7-.6-4.7-2.3-6.4-7-7 4.7-.6 6.4-2.3 7-7Z"
            fill="currentColor"
          />
        </svg>
      )
    case "layers":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3.5 21 8l-9 4.5L3 8l9-4.5Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="m4 12 8 4 8-4M4 16l8 4 8-4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
  }
}

const TrovePaywall = () => {
  const { state, actions } = useAppState()

  const ultimate = state.productData?.ultimate
  const premium = state.productData?.premium
  const locale =
    ultimate?.yearlyProduct?.priceLocale ?? premium?.yearlyProduct?.priceLocale

  const money = (v: number | undefined, fallback: string) =>
    v != null ? formatCurrency(v, locale) : fallback

  const plans = useMemo(
    () => ({
      ultimate: {
        yearly: ultimate?.yearlyProduct?.price ?? 119.99,
        monthlyPrice: ultimate?.monthlyProduct?.price ?? 19.99,
        product: ultimate?.yearlyProduct,
        monthlyProduct: ultimate?.monthlyProduct,
        hasTrial: ultimate?.yearlyProduct?.hasTrial ?? true,
        trialDays: ultimate?.yearlyProduct?.introPricing?.period ?? 7,
      },
      premium: {
        yearly: premium?.yearlyProduct?.price ?? 35.99,
        monthlyPrice: premium?.monthlyProduct?.price ?? 13.99,
        product: premium?.yearlyProduct,
        monthlyProduct: premium?.monthlyProduct,
        hasTrial: premium?.yearlyProduct?.hasTrial ?? true,
        trialDays: premium?.yearlyProduct?.introPricing?.period ?? 7,
      },
    }),
    [ultimate, premium],
  )

  const [tier, setTier] = useState<Tier>("ultimate")
  const [billing, setBilling] = useState<"yearly" | "monthly">("yearly")

  // Animated capacity number that eases toward the active tier's storage.
  const [gb, setGb] = useState(GB_ULTIMATE)
  const gbRef = useRef(GB_ULTIMATE)
  const rafRef = useRef(0)

  // Tint the OS inset bars to blend into the deep violet vault room.
  useEffect(() => {
    actions.setHeaderInsetColor("#14101f")
    actions.setFooterInsetColor("#171125")
  }, [actions])

  useEffect(() => {
    const target = tier === "ultimate" ? GB_ULTIMATE : GB_PREMIUM
    cancelAnimationFrame(rafRef.current)
    const from = gbRef.current
    const dur = 620
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur)
      // easeOutCubic
      const e = 1 - Math.pow(1 - t, 3)
      const val = Math.round(from + (target - from) * e)
      gbRef.current = val
      setGb(val)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [tier])

  const gbLabel =
    gb >= 1000
      ? `${(gb / 1000).toFixed(gb % 1000 === 0 ? 0 : 1)} TB`
      : `${gb} GB`

  const active = plans[tier]
  const yearlyPrice = money(
    active.yearly,
    tier === "ultimate" ? "$119.99" : "$35.99",
  )
  const monthlyOfYear = money(
    active.yearly / 12,
    tier === "ultimate" ? "$9.99" : "$2.99",
  )
  const monthlyPrice = money(
    active.monthlyPrice,
    tier === "ultimate" ? "$19.99" : "$13.99",
  )
  const savePercent = Math.max(
    0,
    Math.round((1 - active.yearly / (active.monthlyPrice * 12)) * 100),
  )

  const selectedProduct =
    billing === "yearly" ? active.product : active.monthlyProduct
  const selectedPriceLine =
    billing === "yearly"
      ? active.hasTrial
        ? `${active.trialDays} days free, then ${yearlyPrice}/year`
        : `${yearlyPrice}/year`
      : `${monthlyPrice}/month`

  const ctaLabel =
    billing === "yearly" && active.hasTrial
      ? `Start ${active.trialDays} days free`
      : tier === "ultimate"
        ? "Get Ultimate"
        : "Get Premium"

  const buy = useCallback(() => {
    if (selectedProduct) native.subscribe(selectedProduct)
  }, [selectedProduct])

  const close = useCallback(() => native.postNativeEvent("close"), [])

  const isUlt = tier === "ultimate"

  // --- Tier lever (vertical slider) -------------------------------------
  // Drag or tap the two detents. Top = Ultimate, bottom = Premium.
  const railRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)

  const setFromClientY = useCallback((clientY: number) => {
    const rail = railRef.current
    if (!rail) return
    const r = rail.getBoundingClientRect()
    const frac = (clientY - r.top) / r.height // 0 top .. 1 bottom
    setTier(frac < 0.5 ? "ultimate" : "premium")
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      draggingRef.current = true
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      setFromClientY(e.clientY)
    },
    [setFromClientY],
  )
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return
      setFromClientY(e.clientY)
    },
    [setFromClientY],
  )
  const endDrag = useCallback((e: React.PointerEvent) => {
    draggingRef.current = false
    ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
  }, [])
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowUp") {
      setTier("ultimate")
      e.preventDefault()
    } else if (e.key === "ArrowDown") {
      setTier("premium")
      e.preventDefault()
    }
  }, [])

  return (
    <div
      className={styles.page}
      data-tier={tier}
      style={
        {
          "--knob": isUlt ? 0 : 1,
        } as React.CSSProperties
      }
    >
      <div className={styles.aura} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <button className={styles.close} onClick={close} aria-label="Not now">
        <Glyph name="close" />
      </button>

      <header className={styles.intro}>
        <span className={styles.kicker}>
          <Glyph name="lock" />
          Private Photo Vault
        </span>
        <h1 className={styles.headline}>
          Room for{" "}
          <span className={styles.headlineSwap}>
            <span className={styles.wordUlt}>everything</span>
            <span className={styles.wordPrem}>the essentials</span>
          </span>
          .
        </h1>
        <p className={styles.sub}>
          One private home for every photo and video — backed up, organized, and
          locked to you alone.
        </p>
      </header>

      <section className={styles.stage}>
        {/* The vault that levels up. */}
        <div className={styles.vault}>
          <div className={styles.vaultHead}>
            <span className={styles.capacity}>
              <span className={styles.capNum}>{gbLabel}</span>
              <span className={styles.capWord}>private storage</span>
            </span>
            <span className={styles.locks} aria-hidden="true">
              <span className={styles.lockChip}>
                <Glyph name="lock" />
              </span>
              <span className={`${styles.lockChip} ${styles.lockSecond}`}>
                <Glyph name="lock" />
              </span>
            </span>
          </div>

          <div className={styles.searchBar} aria-hidden="true">
            <Glyph name="search" />
            <span>Search your memories</span>
          </div>

          <div className={styles.albums}>
            {ALBUMS.map((al, ai) => (
              <div className={styles.album} key={al.label}>
                <span className={styles.albumTab}>
                  <Glyph name="layers" />
                  {al.label}
                </span>
                <div className={styles.tiles}>
                  {al.files.map((f, i) => (
                    <span className={styles.tile} key={f}>
                      <img
                        src={`${process.env.PUBLIC_URL}/nimbus/${f}`}
                        alt=""
                        draggable={false}
                        loading="eager"
                      />
                      {ai === 1 && i === 0 && (
                        <span
                          className={styles.dedupe}
                          aria-label="duplicate cleaned up"
                        >
                          <Glyph name="check" />
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            <span className={styles.allLabel} aria-hidden="true">
              All photos
            </span>
          </div>

          <div className={styles.bloom} aria-hidden="true" />
        </div>

        {/* The tier lever. */}
        <div
          className={styles.lever}
          role="slider"
          aria-label="Choose your plan tier: Ultimate or Premium"
          aria-valuemin={0}
          aria-valuemax={1}
          aria-valuenow={isUlt ? 1 : 0}
          aria-valuetext={isUlt ? "Ultimate" : "Premium"}
          tabIndex={0}
          onKeyDown={onKeyDown}
        >
          <button
            className={`${styles.detent} ${styles.detentTop}`}
            onClick={() => setTier("ultimate")}
            aria-pressed={isUlt}
          >
            <Glyph name="spark" />
            Ultimate
          </button>
          <div
            className={styles.rail}
            ref={railRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <span className={styles.railFill} aria-hidden="true" />
            <span className={styles.knob} aria-hidden="true">
              <span className={styles.knobGrip} />
            </span>
          </div>
          <button
            className={`${styles.detent} ${styles.detentBot}`}
            onClick={() => setTier("premium")}
            aria-pressed={!isUlt}
          >
            Premium
          </button>
        </div>
      </section>

      {/* Feature presentation: three powers that light with the lever. */}
      <div className={styles.powers}>
        <div className={styles.power}>
          <span className={styles.powerHead}>
            Room
            <span className={styles.powerVal}>
              {isUlt ? "1,000 GB" : "50 GB"}
            </span>
          </span>
          <span className={styles.powerSub}>
            {isUlt ? "Space to keep all of it" : "For the essentials"}
          </span>
        </div>
        <div className={`${styles.power} ${isUlt ? "" : styles.powerOff}`}>
          <span className={styles.powerHead}>
            Order
            {!isUlt && <span className={styles.ultBadge}>Ultimate</span>}
          </span>
          <span className={styles.powerSub}>
            Subalbums · search · duplicate cleanup
          </span>
        </div>
        <div className={`${styles.power} ${isUlt ? "" : styles.powerOff}`}>
          <span className={styles.powerHead}>
            Seal
            {!isUlt && <span className={styles.ultBadge}>Ultimate</span>}
          </span>
          <span className={styles.powerSub}>
            Dual lock · alphanumeric passcode
          </span>
        </div>
      </div>

      {/* Pricing — yearly featured for whichever tier the lever selects. */}
      <section className={styles.pricing}>
        <button
          className={`${styles.yearCard} ${
            billing === "yearly" ? styles.yearActive : ""
          }`}
          onClick={() => setBilling("yearly")}
          aria-pressed={billing === "yearly"}
        >
          <span className={styles.yearBadge}>
            {isUlt ? "BEST VALUE" : "GOOD VALUE"} · SAVE {savePercent}%
          </span>
          <span className={styles.yearMain}>
            <span className={styles.yearName}>
              {isUlt ? "Ultimate" : "Premium"} · Yearly
            </span>
            <span className={styles.yearPrice}>{yearlyPrice}</span>
          </span>
          <span className={styles.yearSub}>
            <span>{monthlyOfYear}/mo, billed yearly</span>
            <span>
              {active.hasTrial
                ? `${active.trialDays}-day free trial`
                : "Billed annually"}
            </span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthLine} ${
            billing === "monthly" ? styles.monthActive : ""
          }`}
          onClick={() => setBilling("monthly")}
          aria-pressed={billing === "monthly"}
        >
          Prefer monthly? <strong>{monthlyPrice}/mo</strong>
        </button>

        <button className={styles.cta} onClick={buy}>
          <span>{ctaLabel}</span>
          <Glyph name="arrow" />
        </button>

        <p className={styles.fine}>
          {selectedPriceLine}. Auto-renews until canceled. Cancel anytime.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </section>
    </div>
  )
}

export default TrovePaywall
